import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { 
  rescheduleInstructors,
  rescheduleWorkshopsByInstructor,
  rescheduleSlotsByWorkshop, 
  API_URL 
} from "../../Api/apiUrls";

const Reschedule = ({ appointmentId, onClose }) => {
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch instructors on component mount
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(rescheduleInstructors);
        setInstructors(response.data.data.users.map(user => ({
          value: user.id,
          label: user.name
        })));
      } catch (err) {
        setError("Failed to fetch instructors");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInstructors();
  }, []);

  // Fetch workshops when instructor is selected
  useEffect(() => {
    const fetchWorkshops = async () => {
      if (selectedInstructor) {
        try {
          setIsLoading(true);
          const response = await axios.get(`${rescheduleWorkshopsByInstructor}${selectedInstructor.value}`);
          setWorkshops(response.data.data.Workshop.map(workshop => ({
            value: workshop.id,
            label: workshop.name
          })));
          setSelectedWorkshop(null);
          setSlots([]);
          setSelectedSlot(null);
        } catch (err) {
          setError("Failed to fetch workshops");
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchWorkshops();
  }, [selectedInstructor]);

  // Fetch slots when workshop is selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (selectedWorkshop) {
        try {
          setIsLoading(true);
          const response = await axios.get(`${rescheduleSlotsByWorkshop}${selectedWorkshop.value}`);
          setSlots(response.data.data.WorkshopSlot.map(slot => ({
            value: slot.id,
            label: `${slot.date} - ${slot.startTime} to ${slot.endTime}`,
            date: slot.date,
            start_time: slot.startTime,
            end_time: slot.endTime
          })));
          setSelectedSlot(null);
        } catch (err) {
          setError("Failed to fetch slots");
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchSlots();
  }, [selectedWorkshop]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    if (!selectedSlot) {
      setError("Please select a time slot");
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        "appointment_id": `${appointmentId}`,
        "new_slot_id": `${selectedSlot.value}`,
        "rescheduled": notes
      };

      console.log("Sending payload:", payload); // For debugging

      const response = await axios.post(
        `${API_URL}/appointment/reschedule_appointment/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess("Appointment rescheduled successfully!");
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        setError(response.data.message || "Failed to reschedule appointment");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reschedule appointment");
      console.error("Reschedule error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Reschedule Appointment</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Select Instructor</label>
                <Select
                  options={instructors}
                  value={selectedInstructor}
                  onChange={setSelectedInstructor}
                  placeholder="Select Instructor"
                  isClearable
                  required
                  isLoading={isLoading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Select Workshop</label>
                <Select
                  options={workshops}
                  value={selectedWorkshop}
                  onChange={setSelectedWorkshop}
                  placeholder="Select Workshop"
                  isClearable
                  isDisabled={!selectedInstructor}
                  required
                  isLoading={isLoading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Select Slot</label>
                <Select
                  options={slots}
                  value={selectedSlot}
                  onChange={setSelectedSlot}
                  placeholder="Select Slot"
                  isClearable
                  isDisabled={!selectedWorkshop}
                  required
                  isLoading={isLoading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Reschedule Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Enter reason for rescheduling..."
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || !selectedSlot}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Processing...
                    </>
                  ) : (
                    "Reschedule"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reschedule;